"use client";

import { useState, useEffect, useRef, RefObject } from "react";

function useOnScreen<T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverInit = {},
  once = false
): {
  ref: RefObject<T>;
  isVisible: boolean;
  hasBeenVisible: boolean;
} {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const visible = entry.isIntersecting;
      setIsVisible(visible);

      if (visible && !hasBeenVisible) {
        setHasBeenVisible(true);
      }
      if (once && visible && !hasBeenVisible) {
        if (ref.current) observer.unobserve(ref.current);
      }
    }, options);

    const currentRef = ref.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options, once, hasBeenVisible]);

  return { ref: ref as RefObject<T>, isVisible, hasBeenVisible };
}

export default useOnScreen;
